import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'

export function isStorageObjectUrl(url: string) {
  try {
    return new URL(url, window.location.origin).pathname.startsWith('/api/storage/object/')
  } catch {
    return false
  }
}

export function withInlineStorageUrl(url: string) {
  if (!isStorageObjectUrl(url)) {
    return url
  }

  const parsedUrl = new URL(url, window.location.origin)
  parsedUrl.searchParams.set('inline', '1')
  return parsedUrl.toString()
}

export function StorageTextFilePreview({
  url,
  fileName,
}: {
  url: string
  fileName?: string | null
}) {
  const [content, setContent] = useState<string | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    setContent(null)
    setError(false)

    fetch(url, isStorageObjectUrl(url) ? { credentials: 'include' } : undefined)
      .then((r) => (r.ok ? r.text() : Promise.reject()))
      .then((value) => {
        if (!cancelled) setContent(value)
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })

    return () => {
      cancelled = true
    }
  }, [url])

  if (error) {
    return (
      <div className='flex h-full items-center justify-center text-[13px] text-text-subtle'>
        Failed to load file content
      </div>
    )
  }

  if (content === null) {
    return (
      <div className='flex h-full items-center justify-center'>
        <Loader2 className='h-5 w-5 animate-spin text-text-placeholder' />
      </div>
    )
  }

  return (
    <div className='flex h-full flex-col'>
      <div className='shrink-0 border-b border-border bg-surface-subtle px-4 py-2'>
        <span className='text-[13px] font-medium text-text-secondary'>{fileName}</span>
      </div>
      <ScrollArea className='min-h-0 flex-1' viewClassName='h-full [&>div]:!block'>
        <pre className='whitespace-pre-wrap p-4 text-[12px] text-text-body'>
          {content}
        </pre>
      </ScrollArea>
    </div>
  )
}
